/**
 * useToFilevineSuggestionCard - Hook for managing TO_FILEVINE suggestion card state
 *
 * Consolidates:
 * - UI state (expand, modal)
 * - Action state (accepting, rejecting)
 * - Payload editing (merged from useSuggestionEditor)
 * - Derived data (field updates, confidence, reasoning)
 */

import { useCallback, useMemo, useReducer } from 'react';

import type {
  FilevineEntitySyncSuggestionSchema,
  FilevineEntitySyncSuggestionSetSchema,
  FilevineSectionPayload,
} from '@/services/api/generated/entitySyncService';

import { normalizeConfidence } from '../../utils/confidenceUtils';
import type {
  ActionStatus,
  BaseCardAction,
  FieldUpdate,
  ToFilevineSuggestionCardState,
} from './types';

// ============================================================================
// Types
// ============================================================================

interface UseToFilevineSuggestionCardProps {
  suggestion: FilevineEntitySyncSuggestionSchema;
  parentSet: FilevineEntitySyncSuggestionSetSchema;
  onAccept: (
    suggestion: FilevineEntitySyncSuggestionSchema,
    payload?: FilevineSectionPayload,
  ) => Promise<void>;
  onReject: (id: string) => Promise<void>;
}

interface State {
  isExpanded: boolean;
  isEditModalOpen: boolean;
  actionStatus: ActionStatus;
  editedPayload: FilevineSectionPayload;
  isPayloadModified: boolean;
}

type Action =
  | BaseCardAction
  | { type: 'UPDATE_FIELD'; fieldName: string; value: unknown }
  | { type: 'RESET_PAYLOAD'; payload: FilevineSectionPayload };

// ============================================================================
// Helpers
// ============================================================================

/**
 * Parse suggestion payload into field updates for display
 */
function parsePayloadUpdates(
  payload: Record<string, unknown> | undefined,
): FieldUpdate[] {
  if (!payload) return [];

  // Get updates - may be nested in 'updates' key or at root level
  const updates = (payload.updates ?? payload) as Record<string, unknown>;
  const fieldsToClear = payload.fields_to_clear as string[] | undefined;

  // Process updates (filter out meta keys and undefined values)
  const updateResults: FieldUpdate[] = Object.entries(updates)
    .filter(([key, value]) => {
      if (key === 'fields_to_clear' || key === 'updates') return false;
      return value !== undefined;
    })
    .map(([key, value]) => ({
      fieldKey: key,
      newValue: value,
      isClear: value === null || value === '',
    }));

  // Process fields_to_clear (skip if already in updates)
  const clearResults: FieldUpdate[] =
    fieldsToClear && Array.isArray(fieldsToClear)
      ? fieldsToClear
          .filter((field) => !updateResults.some((r) => r.fieldKey === field))
          .map((field) => ({
            fieldKey: field,
            newValue: null,
            isClear: true,
          }))
      : [];

  return [...updateResults, ...clearResults];
}

/**
 * Get initial payload from suggestion
 */
function getInitialPayload(
  suggestion: FilevineEntitySyncSuggestionSchema,
): FilevineSectionPayload {
  const payload = (suggestion.modified_payload ??
    suggestion.suggested_payload) as FilevineSectionPayload | undefined;
  return payload ?? { updates: {}, fields_to_clear: [] };
}

// ============================================================================
// Reducer
// ============================================================================

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'TOGGLE_EXPAND':
      return { ...state, isExpanded: !state.isExpanded };

    case 'SET_EDIT_MODAL':
      return { ...state, isEditModalOpen: action.open };

    case 'START_ACCEPT':
      return { ...state, actionStatus: 'accepting' };

    case 'START_REJECT':
      return { ...state, actionStatus: 'rejecting' };

    case 'ACTION_COMPLETE':
      return { ...state, actionStatus: 'idle' };

    case 'UPDATE_FIELD': {
      const currentUpdates = state.editedPayload.updates ?? {};
      const newUpdates = {
        ...currentUpdates,
        [action.fieldName]: action.value,
      } as FilevineSectionPayload['updates'];
      return {
        ...state,
        editedPayload: { ...state.editedPayload, updates: newUpdates },
        isPayloadModified: true,
      };
    }

    case 'RESET_PAYLOAD':
      return {
        ...state,
        editedPayload: action.payload,
        isPayloadModified: false,
      };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useToFilevineSuggestionCard({
  suggestion,
  parentSet,
  onAccept,
  onReject,
}: UseToFilevineSuggestionCardProps): ToFilevineSuggestionCardState {
  const defaultExpanded = suggestion.status !== 'COMPLETED';

  const initialPayload = useMemo(
    () => getInitialPayload(suggestion),
    [suggestion],
  );

  const [state, dispatch] = useReducer(reducer, {
    isExpanded: defaultExpanded,
    isEditModalOpen: false,
    actionStatus: 'idle',
    editedPayload: initialPayload,
    isPayloadModified: false,
  });

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const confidence = useMemo(
    () => normalizeConfidence(parentSet.confidence),
    [parentSet.confidence],
  );

  const aiReasoning = suggestion.reasoning ?? undefined;

  const fieldUpdates = useMemo(
    () => parsePayloadUpdates(state.editedPayload as Record<string, unknown>),
    [state.editedPayload],
  );

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const toggleExpand = useCallback(() => {
    dispatch({ type: 'TOGGLE_EXPAND' });
  }, []);

  const setEditModalOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_EDIT_MODAL', open });
  }, []);

  const updateField = useCallback((fieldName: string, value: unknown) => {
    dispatch({ type: 'UPDATE_FIELD', fieldName, value });
  }, []);

  const resetPayload = useCallback(() => {
    dispatch({ type: 'RESET_PAYLOAD', payload: initialPayload });
  }, [initialPayload]);

  const handleAccept = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      // Pass modified payload if edits were made
      const payloadToSend = state.isPayloadModified
        ? state.editedPayload
        : undefined;
      await onAccept(suggestion, payloadToSend);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion, state.isPayloadModified, state.editedPayload]);

  const handleApprove = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      await onAccept(suggestion, undefined);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion]);

  const handleModify = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      await onAccept(suggestion, state.editedPayload);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion, state.editedPayload]);

  const handleReject = useCallback(async () => {
    dispatch({ type: 'START_REJECT' });
    try {
      await onReject(suggestion.id);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onReject, suggestion.id]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // UI state
    isExpanded: state.isExpanded,
    isEditModalOpen: state.isEditModalOpen,

    // Action state
    isAccepting: state.actionStatus === 'accepting',
    isRejecting: state.actionStatus === 'rejecting',
    isActionInProgress: state.actionStatus !== 'idle',

    // Payload editing
    editedPayload: state.editedPayload,
    isPayloadModified: state.isPayloadModified,
    updateField,
    resetPayload,

    // Derived
    confidence,
    aiReasoning,
    fieldUpdates,

    // Actions
    toggleExpand,
    setEditModalOpen,
    handleAccept,
    handleApprove,
    handleModify,
    handleReject,
  };
}

export default useToFilevineSuggestionCard;
